import React, { useEffect, useState } from "react";
import { Box } from "@mui/material";

const ICON_COLOR = "#005EB8";
const POLL_INTERVAL = 600;
const POLL_ATTEMPTS = 5;

const BAG_PATH = "M4.66667 4.43811V5.73335H3.46667C3.28986 5.73335 3.12029 5.80158 2.99526 5.92303C2.87024 6.04448 2.8 6.20921 2.8 6.38096L2 14.1524C2 14.3242 2.07024 14.4889 2.19526 14.6103C2.32029 14.7318 2.48986 14.8 2.66667 14.8H9.71667C9.3849 14.4293 9.12881 13.9895 8.97269 13.5048H3.33333L4.13333 7.02858H4.66667V8.32382H6V7.02858H10V8.32382H11.3333V7.02858H11.8667L12.0871 8.81342C12.1902 8.80454 12.2946 8.80001 12.4 8.80001C12.771 8.80001 13.1288 8.85612 13.4655 8.96031L13.2 6.38096C13.2 6.20921 13.1298 6.04448 13.0047 5.92303C12.8797 5.80158 12.7101 5.73335 12.5333 5.73335H11.3333V4.43811C11.3333 3.57931 10.9821 2.75569 10.357 2.14843C9.7319 1.54117 8.88405 1.20001 8 1.20001C7.11595 1.20001 6.2681 1.54117 5.64298 2.14843C5.01786 2.75569 4.66667 3.57931 4.66667 4.43811ZM10 4.43811V5.73335H6V4.43811C6 3.92283 6.21071 3.42866 6.58579 3.0643C6.96086 2.69994 7.46957 2.49525 8 2.49525C8.53043 2.49525 9.03914 2.69994 9.41421 3.0643C9.78929 3.42866 10 3.92283 10 4.43811Z";
const PLUS_PATH = "M13 11.8H14.8V13H13V14.8H11.8V13H10V11.8H11.8V10H13V11.8Z";

const getLinkUrl = (product) =>
    product?.link_url || product?.data?.linkUrl || product?.linkUrl || product?.link || null;

const quickviewHref = (product) => {
    const linkUrl = getLinkUrl(product);
    if (!linkUrl || typeof window === "undefined") return null;

    try {
        const url = new URL(linkUrl, window.location.origin);
        if (window.location.hostname.includes("development.aeropostale.com")) {
            url.hostname = "development.aeropostale.com";
        }
        url.searchParams.set("source", "quickview");
        return url.toString();
    } catch (error) {
        return null;
    }
};

let clientReady = false;
let pendingInit = null;
const waiting = new Set();

const ensureClientBinding = () => {
    if (clientReady || pendingInit) return;

    let attempts = 0;

    const bind = () => {
        pendingInit = null;

        if (window.initProductTileQuickView) {
            clientReady = true;
            window.initProductTileQuickView();
            waiting.forEach((notify) => notify());
            waiting.clear();
            return;
        }

        attempts += 1;
        if (attempts <= POLL_ATTEMPTS) {
            pendingInit = window.setTimeout(bind, POLL_INTERVAL);
        }
    };

    pendingInit = window.setTimeout(bind, 0);
};

const QuickAdd = ({ product }) => {
    const [ready, setReady] = useState(clientReady);
    const href = quickviewHref(product);
    const productId = product?.product_id || product?.id || null;

    useEffect(() => {
        if (!href || typeof window === "undefined" || clientReady) return undefined;

        const notify = () => setReady(true);
        waiting.add(notify);
        ensureClientBinding();

        return () => {
            waiting.delete(notify);
        };
    }, [href]);

    if (!href || !ready) return null;

    return (
        <Box
            component="span"
            className="quickview"
            data-product-component="quickview"
            data-href={href}
            title="Quick add"
            onClick={(event) => event.preventDefault()}
            sx={{
                flex: "0 0 auto",
                width: "30px",
                height: "30px",
                borderRadius: "999px",
                border: "1px solid",
                borderColor: (theme) => theme.tvp.hairline,
                backgroundColor: (theme) => theme.tvp.card,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                cursor: "pointer",
                transition: "box-shadow 140ms ease",
                "&:hover": { boxShadow: (theme) => theme.tvp.cardHover },
            }}
        >
            <Box component="span" data-itemid={productId || undefined} sx={{ display: "flex" }}>
                <svg width="15" height="15" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path fillRule="evenodd" clipRule="evenodd" d={BAG_PATH} fill={ICON_COLOR} />
                    <path d={PLUS_PATH} fill={ICON_COLOR} />
                </svg>
            </Box>
        </Box>
    );
};

export default QuickAdd;
